<template>
  <div class="role-selector-overlay" v-if="showRoleSelector">
    <div class="role-selector-modal">
      <div class="modal-header">
        <h2 class="modal-title">Pilih Role Anda</h2>
        <p class="modal-subtitle">Anda memiliki beberapa role. Silakan pilih role yang akan digunakan:</p>
      </div>
      
      <div class="roles-grid">
        <div 
          v-for="role in availableRoles" 
          :key="role"
          class="role-card"
          :class="{ 'selected': selectedRole === role }"
          @click="selectRole(role)"
        >
          <div class="role-icon">
            <i :class="getRoleIcon(role)"></i>
          </div>
          <div class="role-info">
            <h3 class="role-name">{{ formatRoleName(role) }}</h3>
            <p class="role-description">{{ getRoleDescription(role) }}</p>
          </div>
        </div>
      </div>
      
      <div class="modal-actions">
        <el-button 
          type="primary" 
          @click="confirmRoleSelection"
          :disabled="!selectedRole"
          size="medium"
        >
          Lanjutkan dengan Role ini
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  name: 'RoleSelector',
  data() {
    return {
      selectedRole: null,
      showRoleSelector: false,
      hasShownInitialRoleSelector: false
    }
  },
  computed: {
    ...mapState(['userRoles', 'info']),
    availableRoles() {
      // Return array of role strings from JWT authorities
      if (Array.isArray(this.userRoles)) {
        return this.userRoles
      }
      return []
    },
    hasMultipleRoles() {
      return this.availableRoles.length > 1
    }
  },
  methods: {
    ...mapMutations(['set_active_role']),
    ...mapActions(['setActiveRole']),
    
    show() {
      if (this.hasMultipleRoles) {
        this.showRoleSelector = true
        this.hasShownInitialRoleSelector = true
        // Auto-select first role as default
        this.selectedRole = this.availableRoles[0]
      } else if (this.availableRoles.length === 1) {
        // If user has only one role, auto-select it
        this.setActiveRole(this.availableRoles[0])
        this.hasShownInitialRoleSelector = true
        this.$emit('role-selected', this.availableRoles[0])
      }
    },

    showManually() {
      // Method to show role selector manually (from role switcher button)
      if (this.hasMultipleRoles) {
        this.showRoleSelector = true
        // Set current active role as selected
        this.selectedRole = this.$store.state.activeRole || this.availableRoles[0]
      }
    },
    
    hide() {
      this.showRoleSelector = false
      this.selectedRole = null
    },

    reset() {
      this.showRoleSelector = false
      this.selectedRole = null
      this.hasShownInitialRoleSelector = false
    },
    
    selectRole(role) {
      this.selectedRole = role
    },
    
    confirmRoleSelection() {
      if (this.selectedRole) {
        this.setActiveRole(this.selectedRole)
        this.$emit('role-selected', this.selectedRole)
        this.hide()
        
        // Show success message
        this.$message.success(`Berhasil beralih ke role ${this.formatRoleName(this.selectedRole)}`)
      }
    },
    
    formatRoleName(role) {
      // Format role name for display
      return role.replace('ROLE_', '').replace('_', ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase())
    },
    
    getRoleDescription(role) {
      const descriptions = {
        'ROLE_ADMIN': 'Akses penuh ke semua fitur sistem',
        'ROLE_CURATOR': 'Dapat mengelola konten dan user',
        'ROLE_EMPLOYEE': 'Akses ke fitur-fitur dasar karyawan',
        'ROLE_MANAGER': 'Akses manajemen untuk unit kerja',
        'ROLE_SUPERVISOR': 'Akses supervisi dan monitoring'
      }
      return descriptions[role] || 'Role dengan akses khusus'
    },
    
    getRoleIcon(role) {
      const icons = {
        'ROLE_ADMIN': 'el-icon-s-tools',
        'ROLE_CURATOR': 'el-icon-edit-outline',
        'ROLE_EMPLOYEE': 'el-icon-user',
        'ROLE_MANAGER': 'el-icon-s-management',
        'ROLE_SUPERVISOR': 'el-icon-view'
      }
      return icons[role] || 'el-icon-user-solid'
    }
  },
  
  mounted() {
    // Check if this is initial login (no active role set)
    if (this.availableRoles.length > 0 && !this.$store.state.activeRole) {
      this.show()
    }
  },
  
  watch: {
    userRoles: {
      immediate: true,
      handler(newRoles) {
        // Only auto-show if user has multiple roles and no active role is set (initial login)
        // and we haven't shown the initial role selector yet
        if (newRoles && newRoles.length > 1 && !this.$store.state.activeRole && !this.hasShownInitialRoleSelector) {
          this.$nextTick(() => {
            this.show()
          })
        } else if (newRoles && newRoles.length === 1 && !this.$store.state.activeRole && !this.hasShownInitialRoleSelector) {
          // Auto-select single role
          this.setActiveRole(newRoles[0])
          this.hasShownInitialRoleSelector = true
          this.$emit('role-selected', newRoles[0])
        }
      }
    },
    
    // Watch for logout to reset component state
    '$store.state.token': {
      handler(newToken) {
        if (!newToken) {
          // User logged out, reset component state
          this.reset()
        }
      }
    }
  }
}
</script>

<style scoped>
.role-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.role-selector-modal {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.modal-header {
  text-align: center;
  margin-bottom: 2rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.modal-subtitle {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.role-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.role-card:hover {
  border-color: #409EFF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.role-card.selected {
  border-color: #409EFF;
  background: #ecf5ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.role-icon {
  text-align: center;
  margin-bottom: 1rem;
}

.role-icon i {
  font-size: 2.5rem;
  color: #409EFF;
}

.role-info {
  text-align: center;
}

.role-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.role-description {
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
}

.modal-actions {
  text-align: center;
}

/* Responsive design */
@media (max-width: 768px) {
  .role-selector-modal {
    margin: 1rem;
    padding: 1.5rem;
  }
  
  .roles-grid {
    grid-template-columns: 1fr;
  }
}
</style>
